import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, ArrowLeft, Play, Tv, Radio, ExternalLink, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_LABELS = {
  7: 'Grade 7 — Kansas History',
  8: 'Grade 8 — U.S. History',
  9: 'Grade 9 — World History',
  10: 'Grade 10 — Modern World History',
  11: 'Grade 11 — United States History',
  12: 'Grade 12 — Government & Economics',
};

export default function VideoIndex() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialGrade = searchParams.get('grade') ? parseInt(searchParams.get('grade')) : null;
  const [selectedGrade, setSelectedGrade] = useState(initialGrade);
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [vRes, cRes] = await Promise.all([
        axios.get(`${API}/videos${selectedGrade ? `?grade=${selectedGrade}` : ''}`),
        axios.get(`${API}/videos/channels`),
      ]);
      setVideos(vRes.data);
      setChannels(cRes.data.current_events || []);
    } catch {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  }, [selectedGrade]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Group videos by grade then unit
  const grouped = {};
  videos.forEach(v => {
    const gKey = v.grade_level;
    if (!grouped[gKey]) grouped[gKey] = {};
    const uKey = v.unit_number;
    if (!grouped[gKey][uKey]) grouped[gKey][uKey] = { title: v.unit_title, videos: [] };
    grouped[gKey][uKey].videos.push(v);
  });

  const grades = [7, 8, 9, 10, 11, 12];

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Resources</p>
          <h1 className="font-serif-display text-4xl text-[#14213D] tracking-tight flex items-center gap-3">
            <Play className="h-7 w-7 text-[#D4AF37]" /> Video Library
          </h1>
          <p className="text-sm text-[#5A6577] mt-2 max-w-2xl">Curated YouTube lessons, news broadcasts, and documentary clips tied to each unit.</p>
        </div>

        {/* Current Events Section */}
        <section className="mb-10">
          <h2 className="font-serif-display text-xl text-[#14213D] mb-4 flex items-center gap-2">
            <Radio className="h-5 w-5 text-red-600" /> Current Events &mdash; Daily Broadcasts
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {channels.map(ch => (
              <a
                key={ch.name}
                href={ch.channel_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
                data-testid={`channel-${ch.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <Card className="p-5 border border-[#EBE7E0] hover:shadow-lg hover:border-red-200 transition-all flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center shrink-0 group-hover:bg-red-200 transition-colors">
                    <Tv className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#14213D] group-hover:text-red-800 transition-colors text-sm">{ch.name}</h3>
                    <p className="text-xs text-[#5A6577] mt-0.5">{ch.description}</p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-[#A0AEC0] group-hover:text-red-600 shrink-0 transition-colors" />
                </Card>
              </a>
            ))}
          </div>
        </section>

        {/* Grade filter */}
        <section className="mb-6">
          <h2 className="text-lg font-serif text-[#14213D] mb-4 flex items-center gap-2">
            <Play className="h-5 w-5 text-[#14213D]" /> Crash Course Videos by Grade
          </h2>
          <div className="flex flex-wrap gap-2 mb-6">
            <Button
              variant={selectedGrade === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedGrade(null)}
              className={selectedGrade === null ? 'bg-[#14213D] text-[#FDFBF7]' : ''}
              data-testid="grade-filter-all"
            >
              All Grades
            </Button>
            {grades.map(g => (
              <Button
                key={g}
                variant={selectedGrade === g ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedGrade(g)}
                className={selectedGrade === g ? 'bg-[#14213D] text-[#FDFBF7]' : ''}
                data-testid={`grade-filter-${g}`}
              >
                Grade {g}
              </Button>
            ))}
          </div>
        </section>

        {/* Videos by grade */}
        {loading ? (
          <div className="text-center py-12 text-[#5A6577]">Loading videos...</div>
        ) : (
          <div className="space-y-10">
            {(selectedGrade ? [selectedGrade] : grades).map(g => {
              const gradeData = grouped[g];
              if (!gradeData) return null;
              return (
                <div key={g} data-testid={`video-grade-section-${g}`}>
                  <h3 className="text-base font-serif text-[#14213D] mb-4 pb-2 border-b border-[#EBE7E0]">
                    {GRADE_LABELS[g]}
                  </h3>
                  <div className="space-y-4">
                    {Object.entries(gradeData).sort(([a],[b]) => Number(a) - Number(b)).map(([unitNum, unitData]) => (
                      <div key={`${g}-${unitNum}`}>
                        <p className="text-xs font-semibold text-[#14213D] mb-2 uppercase tracking-wider">
                          Unit {unitNum}: {unitData.title}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {unitData.videos.map(v => (
                            <Card
                              key={v.id}
                              className="overflow-hidden border border-[#EBE7E0] hover:shadow-md transition-shadow group cursor-pointer"
                              onClick={() => setPlayingId(playingId === v.id ? null : v.id)}
                              data-testid={`video-card-${v.id}`}
                            >
                              {playingId === v.id ? (
                                <div className="aspect-video">
                                  <iframe
                                    src={`https://www.youtube.com/embed/${v.youtube_id}?autoplay=1`}
                                    title={v.title}
                                    className="w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                  />
                                </div>
                              ) : (
                                <div className="relative aspect-video bg-gradient-to-br from-[#14213D] to-[#0F1A2E]">
                                  <img
                                    src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`}
                                    alt={v.title}
                                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-12 h-12 bg-red-600/90 rounded-full flex items-center justify-center group-hover:bg-red-600 transition-colors shadow-lg">
                                      <Play className="h-5 w-5 text-white ml-0.5" fill="white" />
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div className="p-3">
                                <h4 className="text-xs font-medium text-[#14213D] line-clamp-2 leading-snug">{v.title}</h4>
                                <p className="text-[11px] text-[#A0AEC0] mt-1">{v.series} &middot; {v.duration}</p>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
